import type { ActionContext, ActionDefinitions, Actions } from './Action';
import { recurrenceDefinitionActionDefinitions } from 'pages/apps/platform/recurrence_definitions/recurrenceDefinitionActions';
import { schedulingWindowActionDefinitions } from 'pages/apps/platform/scheduling_window/schedulingWindowActions';
import { folderActionDefinitions } from 'pages/apps/platform/folders/folderActions';
import { zoneActionDefinitions } from 'pages/apps/platform/zones/zoneActions';
import { lockActionDefinitions } from 'pages/apps/platform/locks/lockActions';
import { queueActionDefinitions } from 'pages/apps/platform/queues/queueActions';
import { triggerActionDefinitions } from 'pages/apps/platform/triggers/triggerActions';
import { integrationActionDefinitions } from 'pages/apps/platform/integrations/integrationActions';
import { dashboardActionDefinitions } from 'pages/apps/platform/monitoring/dashboards/dashboardActions';
import { nodeDefinitionActionDefinitions } from 'pages/apps/platform/customer_node_definitions/nodeDefinitionActions';
import { eventActionDefinitions } from 'pages/apps/platform/events/eventActions';
import { pythonLibraryActionDefinitions } from 'pages/apps/platform/python_libraries/pythonLibraryActions';
import { tableActionDefinitions } from 'pages/apps/platform/tables/tableActions';
import { connectionActionDefinitions } from 'pages/apps/platform/connections/connectionActions';
import { workerActionDefinitions } from 'pages/apps/platform/workers/workerActions';
import { reportDefinitionActionDefinitions } from 'pages/apps/platform/reports/reportDefinitionActions';
import { userActionDefinitions } from 'pages/apps/platform/users/userActions';
import { roleActionDefinitions } from 'pages/apps/platform/roles/roleActions';
import { configItemActionDefinitions } from 'pages/apps/platform/config/configItemActions';

/**
 * Every action definition known to the app — per-type definition lists plus any
 * cross-type globals, concatenated in render order. Grows by one list per
 * object type as the registry is adopted.
 */
export const allActionDefinitions: ActionDefinitions = [
  ...recurrenceDefinitionActionDefinitions,
  ...schedulingWindowActionDefinitions,
  ...folderActionDefinitions,
  ...zoneActionDefinitions,
  ...lockActionDefinitions,
  ...queueActionDefinitions,
  ...triggerActionDefinitions,
  ...integrationActionDefinitions,
  ...dashboardActionDefinitions,
  ...nodeDefinitionActionDefinitions,
  ...eventActionDefinitions,
  ...pythonLibraryActionDefinitions,
  ...tableActionDefinitions,
  ...connectionActionDefinitions,
  ...workerActionDefinitions,
  ...reportDefinitionActionDefinitions,
  ...userActionDefinitions,
  ...roleActionDefinitions,
  ...configItemActionDefinitions,
];

const noop = (): void => {};

/**
 * Resolve a flat list of action definitions against the current context into a
 * name-keyed record of ready-to-fire actions.
 *
 * Each definition's `resolve` yields a bound `() => void` (enabled), `'disabled'`
 * (present but not allowed now), or `'hidden'` (not applicable — omitted).
 * Results are keyed by `id`.
 *
 * Two definitions may share an `id` only if at most one is non-hidden for any
 * given context — the convention for plain contextual names (`edit`, `delete`),
 * which return `'hidden'` outside their own type, so a homogeneous selection
 * yields exactly one. Scoped actions that are never hidden (`createX`,
 * `addToTransportX`) carry unique, fully-qualified ids. On a genuine clash an
 * enabled action always beats a disabled one and the first wins among equals; a
 * dev-mode warning flags two enabled collisions (a configuration bug).
 */
export const getActions = (ctx: ActionContext, definitions: ActionDefinitions = allActionDefinitions): Actions => {
  const actions: Actions = {};
  for (const def of definitions) {
    const result = def.resolve(ctx);
    if (result === 'hidden') continue;
    const enabled = typeof result === 'function';

    const existing = actions[def.id];
    if (existing) {
      if (existing.enabled) {
        if (enabled && import.meta.env.DEV) console.warn(`getActions: two enabled actions share id "${def.id}"; keeping the first.`);
        continue; // keep the existing enabled action
      }
      if (!enabled) continue; // both disabled — keep the first
      // existing disabled, this one enabled — fall through to replace it
    }

    actions[def.id] = {
      id: def.id,
      label: def.label,
      icon: def.icon,
      scope: def.scope ?? 'instance',
      enabled,
      execute: typeof result === 'function' ? result : noop,
    };
  }
  return actions;
};
